import { useEffect, useState } from 'react'
import type { FamilySession } from '@core/session'
import { flushQueue } from '@core/documents'
import { refreshBadge } from '@core/badge'
import { Button, Screen } from '@design/components'
import { SendFlow } from '@features/send/SendFlow'
import { RecentlySent } from '@features/send/RecentlySent'
import { ForYou } from '@features/todos/ForYou'
import { NextAppointments } from '@features/calendar/NextAppointments'
import { SharedSpaces } from '@features/expenses/SharedSpaces'
import { ProfileSheet } from './ProfileSheet'

/**
 * The family-member interface. One screen. No tab bar, no search, no filters,
 * no totals (docs/spec.md §6).
 *
 * Resist adding to it. Every control here is a reason for a parent to hesitate,
 * and hesitation is how this loses to WhatsApp.
 */
export function MemberShell({
  session,
  onSignOut,
}: {
  session: FamilySession
  onSignOut: () => void
}) {
  const [sending, setSending] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // iOS gives a PWA no background sync, so the queue can only move while the
  // app is open. Flush on mount, on reconnect, and when the app comes back to
  // the foreground.
  // The number on the icon: what has been handed to this parent, nothing more.
  useEffect(() => {
    void refreshBadge(session.userId, false)
  }, [session.userId, refreshKey])

  useEffect(() => {
    void flushQueue()
    const onOnline = () => void flushQueue()
    const onVisible = () => document.visibilityState === 'visible' && void flushQueue()
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return (
    <>
      <header className="shrink-0 pad-safe-top px-4 pb-2 flex items-center justify-between">
        <h1 className="text-xl font-semibold">{greeting(session.displayName)}</h1>
        <button
          onClick={() => setProfileOpen(true)}
          aria-label="Instellingen"
          className="w-9 h-9 shrink-0 rounded-full bg-ink text-canvas grid place-items-center text-sm font-medium"
        >
          {(session.displayName[0] ?? '?').toUpperCase()}
        </button>
      </header>

      <Screen className="flex flex-col">
        {/* Above the button, because it is the only thing that can be more
            urgent than sending something — and it is absent on most days. */}
        <ForYou userId={session.userId} refreshKey={refreshKey} />

        {/* The other end of the reminder. The evening before an appointment a
            parent is told "Morgen: tandarts om 14:00" — and until this was here,
            opening the app showed a screen that did not mention it. Information,
            not a control: nothing to tap, and absent on most days. */}
        <NextAppointments refreshKey={refreshKey} />

        {/* Only appears when a space has been opened up. On a family where
            nobody shares anything, this screen is unchanged. */}
        <SharedSpaces refreshKey={refreshKey} />

        {/* Primary actions in the lower two-thirds: thumb-first. */}
        <div className="flex-1 min-h-2" />

        <div className="space-y-4">
          {/*
            One button, because there is only one thing to do.

            There used to be a disabled "Betaling toevoegen" beside this,
            greyed out and labelled "komt in een volgende versie". A parent
            wanting to send a photo of a bill read "toevoegen" as the obvious
            choice, pressed it, and nothing happened — so the app looked
            broken while the working button sat right above it.

            A control that cannot be used is not a preview of the roadmap, it
            is an obstacle. It comes back when it does something.
          */}
          <Button size="large" onClick={() => setSending(true)}>
            Document versturen
          </Button>
        </div>

        <div className="flex-1 min-h-6" />

        <RecentlySent refreshKey={refreshKey} />
      </Screen>

      {sending && (
        <SendFlow
          session={session}
          onClose={() => {
            setSending(false)
            setRefreshKey((k) => k + 1)
          }}
        />
      )}

      {profileOpen && (
        <ProfileSheet
          session={session}
          onClose={() => setProfileOpen(false)}
          onSignOut={onSignOut}
        />
      )}
    </>
  )
}

function greeting(name: string) {
  const h = new Date().getHours()
  const part = h < 6 ? 'Goedenacht' : h < 12 ? 'Goeiemorgen' : h < 18 ? 'Goeiemiddag' : 'Goeieavond'
  return name ? `${part}, ${name.split(' ')[0]}` : part
}
